
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Upload, File, X, ExternalLink } from "lucide-react";
import { Client } from "@/types/client";
import { toast } from "sonner";
import { useExternalFileUpload } from "@/hooks/useExternalFileUpload";
import { Progress } from "@/components/ui/progress";

type FormData = Omit<Client, "created_at" | "updated_at" | "client_assignments">;

interface AttachmentsTabProps {
  formData: FormData;
  onFormChange: (data: Partial<FormData>) => void;
}

const attachmentTypes = [
  { key: 'passport', label: 'جوازات المسافر' },
  { key: 'international_flight', label: 'الطيران الدولي' },
  { key: 'domestic_flight', label: 'الطيران الداخلي' },
  { key: 'hotel_booking', label: 'حجوزات الفنادق' },
  { key: 'transport', label: 'المواصلات' },
  { key: 'invoices', label: 'الفواتير' },
  { key: 'other', label: 'أخرى' }
];

export const AttachmentsTab = ({ formData, onFormChange }: AttachmentsTabProps) => {
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const { uploadToExternalService, isUploading, getUploadStatus } = useExternalFileUpload();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFiles(prev => [...prev, type]);

    try {
      const uploadedFile = await uploadToExternalService(
        file,
        type,
        formData.id || 'new-client'
      );

      if (uploadedFile) {
        const currentAttachments = formData.attachments || [];
        const updatedAttachments = [...currentAttachments];
        
        // إزالة المرفق السابق من نفس النوع إن وجد
        const existingIndex = updatedAttachments.findIndex(att => att.startsWith(`${type}:`));
        if (existingIndex > -1) {
          updatedAttachments.splice(existingIndex, 1);
        }
        
        // إضافة المرفق الجديد مع رابط المشاهدة
        const attachmentData = `${type}:${uploadedFile.filename}:${uploadedFile.viewLink}:${uploadedFile.downloadLink}`;
        updatedAttachments.push(attachmentData);
        
        onFormChange({ attachments: updatedAttachments });
        toast.success("تم رفع الملف بنجاح");
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error("حدث خطأ أثناء رفع الملف");
    } finally {
      setUploadingFiles(prev => prev.filter(t => t !== type));
    }
  };

  const removeAttachment = (type: string) => {
    const currentAttachments = formData.attachments || [];
    const updatedAttachments = currentAttachments.filter(att => !att.startsWith(`${type}:`));
    onFormChange({ attachments: updatedAttachments });
  };

  const getAttachmentInfo = (type: string) => {
    const currentAttachments = formData.attachments || [];
    const attachment = currentAttachments.find(att => att.startsWith(`${type}:`));
    if (!attachment) return null;
    
    const parts = attachment.split(':');
    return {
      filename: parts[1],
      viewLink: parts[2],
      downloadLink: parts[3]
    };
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground mb-1">المرفقات</h3>
        <p className="text-sm text-muted-foreground">
          الحد الأقصى لحجم كل ملف: 2 ميغابايت • يتم ضغط الملفات تلقائياً • التخزين الآمن على Supabase
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {attachmentTypes.map((type) => {
          const attachmentInfo = getAttachmentInfo(type.key);
          const isCurrentlyUploading = uploadingFiles.includes(type.key);
          const uploadStatus = getUploadStatus(type.key);

          return (
            <div key={type.key} className="space-y-2">
              <Label className="text-sm font-medium">{type.label}</Label>
              <div className="space-y-2">
                {attachmentInfo ? (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-lg border">
                    <File className="w-4 h-4 text-primary" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm truncate block">{attachmentInfo.filename}</span>
                      <div className="flex gap-1 mt-1">
                        {attachmentInfo.viewLink && (
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            onClick={() => window.open(attachmentInfo.viewLink, '_blank')}
                            className="p-0 h-auto text-xs"
                          >
                            <ExternalLink className="w-3 h-3 ml-1" />
                            عرض
                          </Button>
                        )}
                        {attachmentInfo.downloadLink && (
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            onClick={() => window.open(attachmentInfo.downloadLink, '_blank')}
                            className="p-0 h-auto text-xs"
                          >
                            تحميل
                          </Button>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(type.key)}
                      className="p-1 h-auto hover:bg-destructive/10"
                    >
                      <X className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      type="file"
                      onChange={(e) => handleFileUpload(e, type.key)}
                      disabled={isCurrentlyUploading}
                      className="hidden"
                      id={`file-${type.key}`}
                      accept="image/*,application/pdf,.doc,.docx,.xlsx,.ppt,.pptx"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById(`file-${type.key}`)?.click()}
                      disabled={isCurrentlyUploading}
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 ml-2" />
                      {isCurrentlyUploading ? 'جاري الرفع والضغط...' : 'رفع ملف'}
                    </Button>
                    
                    {uploadStatus && uploadStatus.isUploading && (
                      <div className="space-y-1">
                        <Progress value={uploadStatus.progress} className="h-2" />
                        <p className="text-xs text-muted-foreground text-center">
                          {uploadStatus.progress}% مكتمل
                        </p>
                      </div>
                    )}
                    
                    {uploadStatus?.error && (
                      <p className="text-xs text-destructive">{uploadStatus.error}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
